import { useEffect, useMemo, useState } from 'react';
import atcCodeAPI from '../api/atcCodeAPI';

// ATC reference data changes only when BIR issues a circular, so it is cached
// per (formType, asOf) for the life of the page rather than refetched per mount.
const cache = new Map();

/**
 * Load the ATC codes in force for a form.
 *
 * Pass `payeeClass` to narrow to the codes valid for that payee — BIR issues
 * ATCs in individual/corporate pairs (WI###/WC###), so an individual taxpayer
 * should never be offered a WC code. Omit it to get every code for the form.
 *
 * @returns {{
 *   options: Array<{value:string,label:string,description:string,ratePercent:number}>,
 *   byCode: Record<string, {ratePercent:number, description:string, issuance:string}>,
 *   loading: boolean,
 *   error: string|null
 * }}
 *
 * `options` includes a leading blank entry so it can drop straight into the
 * existing <select> call sites.
 */
export default function useAtcCodes(formType, { asOf, payeeClass } = {}) {
    const key = `${formType || 'ALL'}|${asOf || 'now'}|${payeeClass || 'ALL'}`;
    const [rows, setRows] = useState(() => cache.get(key) || null);
    const [error, setError] = useState(null);

    useEffect(() => {
        let cancelled = false;

        if (cache.has(key)) {
            setRows(cache.get(key));
            setError(null);
            return undefined;
        }

        setRows(null);
        setError(null);

        atcCodeAPI
            .list({ formType, asOf, payeeClass })
            .then((data) => {
                if (cancelled) return;
                cache.set(key, data);
                setRows(data);
            })
            .catch((err) => {
                if (cancelled) return;
                setError(typeof err === 'string' ? err : 'Failed to load ATC codes');
                setRows([]);
            });

        return () => {
            cancelled = true;
        };
    }, [key, formType, asOf, payeeClass]);

    const options = useMemo(() => {
        const list = rows || [];
        return [
            { value: '', label: '— Select ATC —', description: '', ratePercent: null },
            ...list.map((r) => ({
                value: r.code,
                // The ⚠ marks a rate that was never traced to a BIR issuance.
                // It belongs in the label, not a tooltip: this is the last point
                // before the number lands on a filing.
                label: `${r.code} - ${r.ratePercent}%${r.needsReview ? ' ⚠ unverified' : ''}`,
                description: r.needsReview
                    ? `${r.description} — UNVERIFIED RATE (${r.issuance}). Confirm before filing.`
                    : r.description,
                ratePercent: r.ratePercent,
                needsReview: Boolean(r.needsReview),
            })),
        ];
    }, [rows]);

    const byCode = useMemo(() => {
        const map = {};
        for (const r of rows || []) {
            map[r.code] = {
                ratePercent: r.ratePercent,
                description: r.description,
                issuance: r.issuance,
                needsReview: Boolean(r.needsReview),
            };
        }
        return map;
    }, [rows]);

    return { options, byCode, loading: rows === null, error };
}
